import { supabase } from "../lib/supabase";
import {
  deleteContainerDocumentFile,
  uploadMasterContainerDocument,
} from "../utils/containerDocumentsUtils";

export interface MasterContainerDocument {
  id: number;
  master_container_id: number;
  file_path: string;
  file_name: string;
  file_size: number | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export async function getMasterContainerDocument(
  masterContainerId: number
): Promise<{
  success: boolean;
  data: MasterContainerDocument | null;
  error: unknown;
}> {
  try {
    const { data, error } = await supabase
      .from("master_container_documents")
      .select("*")
      .eq("master_container_id", masterContainerId)
      .maybeSingle();

    if (error) throw error;

    return {
      success: true,
      data: (data as MasterContainerDocument | null) ?? null,
      error: null,
    };
  } catch (error) {
    console.error("Error fetching master container document:", error);
    return { success: false, data: null, error };
  }
}

export interface MasterLinkForContainer {
  masterContainerId: number;
  masterReference: string | null;
}

/** Linked Master BL/AWB for a container (with or without uploaded AWB/BL). */
export async function getMasterLinkForContainer(
  containerId: number
): Promise<{
  success: boolean;
  data: MasterLinkForContainer | null;
  error: unknown;
}> {
  try {
    const { data: links, error: linkError } = await supabase
      .from("master_containers_containers")
      .select("master_container_id, master_containers(id, reference)")
      .eq("container_id", containerId)
      .limit(1);

    if (linkError) throw linkError;

    const link = links?.[0];
    if (!link?.master_container_id) {
      return { success: true, data: null, error: null };
    }

    const master = link.master_containers as
      | { id: number; reference: string | null }
      | { id: number; reference: string | null }[]
      | null;
    const masterRef = Array.isArray(master)
      ? master[0]?.reference
      : master?.reference;

    return {
      success: true,
      data: {
        masterContainerId: link.master_container_id,
        masterReference: masterRef ?? null,
      },
      error: null,
    };
  } catch (error) {
    console.error("Error resolving master link for container:", error);
    return { success: false, data: null, error };
  }
}

/** Resolve AWB/BL for a container via its linked master BL/AWB. */
export async function getMasterDocumentForContainer(
  containerId: number
): Promise<{
  success: boolean;
  data: (MasterContainerDocument & { master_reference?: string | null }) | null;
  error: unknown;
}> {
  try {
    const linkResult = await getMasterLinkForContainer(containerId);
    if (!linkResult.success || !linkResult.data) {
      return {
        success: linkResult.success,
        data: null,
        error: linkResult.error,
      };
    }

    const { masterContainerId, masterReference } = linkResult.data;
    const docResult = await getMasterContainerDocument(masterContainerId);
    if (!docResult.success || !docResult.data) {
      return {
        success: docResult.success,
        data: null,
        error: docResult.error,
      };
    }

    return {
      success: true,
      data: {
        ...docResult.data,
        master_reference: masterReference,
      },
      error: null,
    };
  } catch (error) {
    console.error("Error resolving master document for container:", error);
    return { success: false, data: null, error };
  }
}

export async function upsertMasterContainerDocument(
  masterContainerId: number,
  filePath: string,
  fileName: string,
  fileSize?: number
): Promise<{
  success: boolean;
  data: MasterContainerDocument | null;
  error: unknown;
}> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const existing = await getMasterContainerDocument(masterContainerId);
    if (existing.data) {
      const oldPath = existing.data.file_path;
      const { data, error } = await supabase
        .from("master_container_documents")
        .update({
          file_path: filePath,
          file_name: fileName,
          file_size: fileSize ?? null,
          created_by: user?.id ?? null,
        })
        .eq("master_container_id", masterContainerId)
        .select()
        .single();

      if (error) throw error;

      if (oldPath && oldPath !== filePath) {
        await deleteContainerDocumentFile(oldPath);
      }

      return {
        success: true,
        data: data as MasterContainerDocument,
        error: null,
      };
    }

    const { data, error } = await supabase
      .from("master_container_documents")
      .insert([
        {
          master_container_id: masterContainerId,
          file_path: filePath,
          file_name: fileName,
          file_size: fileSize ?? null,
          created_by: user?.id ?? null,
        },
      ])
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      data: data as MasterContainerDocument,
      error: null,
    };
  } catch (error) {
    console.error("Error upserting master container document:", error);
    return { success: false, data: null, error };
  }
}

export async function uploadAndSaveMasterContainerDocument(
  masterContainerId: number,
  file: File
): Promise<{
  success: boolean;
  data: MasterContainerDocument | null;
  error: unknown;
}> {
  const uploadResult = await uploadMasterContainerDocument(
    masterContainerId,
    file
  );
  if (!uploadResult || uploadResult.error || !uploadResult.path) {
    return {
      success: false,
      data: null,
      error: uploadResult?.error ?? new Error("Upload failed"),
    };
  }

  return upsertMasterContainerDocument(
    masterContainerId,
    uploadResult.path,
    file.name,
    file.size
  );
}

/** Batch check which masters have an AWB/BL file (one query). */
export async function getMasterContainerIdsWithDocuments(
  masterContainerIds: number[]
): Promise<{ success: boolean; data: Set<number>; error: unknown }> {
  try {
    if (masterContainerIds.length === 0) {
      return { success: true, data: new Set(), error: null };
    }

    const { data, error } = await supabase
      .from("master_container_documents")
      .select("master_container_id")
      .in("master_container_id", masterContainerIds);

    if (error) throw error;

    return {
      success: true,
      data: new Set(
        (data ?? []).map((row) => row.master_container_id as number)
      ),
      error: null,
    };
  } catch (error) {
    console.error("Error fetching master document upload status:", error);
    return { success: false, data: new Set(), error };
  }
}

export async function deleteMasterContainerDocument(
  masterContainerId: number
): Promise<{ success: boolean; error: unknown }> {
  try {
    const existing = await getMasterContainerDocument(masterContainerId);
    if (!existing.data) {
      return { success: true, error: null };
    }

    const { error } = await supabase
      .from("master_container_documents")
      .delete()
      .eq("master_container_id", masterContainerId);

    if (error) throw error;

    if (existing.data.file_path) {
      await deleteContainerDocumentFile(existing.data.file_path);
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting master container document:", error);
    return { success: false, error };
  }
}
